import { PhoneNumberFormat, PhoneNumberUtil, PhoneNumberType } from 'google-libphonenumber';

const phoneUtil = PhoneNumberUtil.getInstance();

export const TOLL_FREE_PREFIXES = ['800', '833', '844', '855', '866', '877', '888'];
export const INTERNAL_DID_PREFIXES = ['122', '124', '112', '114'];

export const enum DidTypes {
  US_LANDLINE = 'Landline',
  US_TOLLFREE = 'Toll Free',
  US_MOBILE = 'Mobile',
  MOBILE = 'MOBILE',
  GEOGRAPHIC = 'GEOGRAPHIC',
  NONGEOGRAPHIC = 'NONGEOGRAPHIC',
  FREEPHONE = 'FREEPHONE',
  PREMIUM = 'PREMIUM',
  UNKNOWN = 'UNKNOWN',
}

export function isDomesticDid(did: string) {
  if (!did) return false;

  return did.startsWith('+1');
}

export function isTollFreeDid(did: string) {
  return didMatchesPrefixList(did, TOLL_FREE_PREFIXES);
}

export function isInternalDid(did: string) {
  return didMatchesPrefixList(did, INTERNAL_DID_PREFIXES);
}

export type OutgoingCallerDidRejection = 'internal' | 'not-owned';

/**
 * User-facing copy for each rejection reason. Exhaustive by construction: the
 * Record type forces a message for every OutgoingCallerDidRejection, so adding
 * a new reason to the union is a compile error until its message is supplied —
 * and callers that look up `[reason]` pick it up with no branching changes.
 */
export const OUTGOING_CALLER_DID_REJECTION_MESSAGES: Record<OutgoingCallerDidRejection, string> = {
  internal: 'Internal numbers cannot be used as an outgoing caller DID.',
  'not-owned': 'Outgoing caller DID must be one of the customer’s assigned numbers.',
};

/**
 * SDBB-161: the SINGLE source of truth for the outgoing-caller-DID rule.
 * Returns the reason a candidate is NOT allowed, or null when it IS allowed.
 * An outgoing caller DID is allowed only if it is one of the customer's own TNs
 * and is not an internal DID. Both call sites derive from this function — the
 * frontend option filter (via isAllowedOutgoingCallerDid) and the backend gate
 * (which uses the reason to build an accurate error) — so the displayed set and
 * the accepted set cannot drift. Normalizes both sides with cleanPortalDid so
 * formatting differences never cause false rejects.
 */
export function getOutgoingCallerDidRejection(candidate: string, customerTns: string[]): OutgoingCallerDidRejection | null {
  const cleaned = cleanPortalDid(candidate);
  if (!cleaned) {
    return 'not-owned';
  }
  if (isInternalDid(cleaned)) {
    return 'internal';
  }
  if (!customerTns.map(cleanPortalDid).includes(cleaned)) {
    return 'not-owned';
  }
  return null;
}

/**
 * Boolean form of getOutgoingCallerDidRejection for use as a list filter
 * predicate (e.g. building the frontend dropdown options).
 */
export function isAllowedOutgoingCallerDid(candidate: string, customerTns: string[]): boolean {
  return getOutgoingCallerDidRejection(candidate, customerTns) === null;
}

export function isOperatorNumber(number: string) {
  return number === '0' || number === '10';
}

export function isShortNumber(number: string) {
  return number.match(/^1{0,1}[2-9]11$/);
}

export function getDidType(did: string): string {
  if (isInternalDid(did)) {
    return 'internal-did';
  }

  if (isTollFreeDid(did)) {
    return 'tf-did';
  }

  return 'did';
}

export function didMatchesPrefixList( did: string, prefixes: string[]) {
  if (!did) {
    return false;
  }

  const prefix = did.substring(0, 3);
  return prefixes.indexOf(prefix) !== -1;
}

export function toE164Default10DigitUs(did: string) {
  if (!did || did.startsWith('+')) {
    return did;
  }

  return did.length === 10 ? `+1${did}` : `+${did}`;
}

export function toDeprecatedNonE164(tnE164: string) {
  if (!tnE164) {
    return tnE164;
  }

  // if starts with +1 and is 11 digits, remove the +1
  if (tnE164.startsWith('+1') && tnE164.length === 12) {
    return tnE164.substring(2);
  }

  // if starts with +, remove the +
  if (tnE164.startsWith('+')) {
    return tnE164.substring(1);
  }

  return tnE164;
}

// TODO: analyze where we use this,
// and see if can replace with the cleanPortalDid (rename cleanPortalDid to cleanDid)
export function cleanDid(did: string) {
  // strip common characters added to phone number
  return did && did.replace(/[-.()]/g, '');
}

// Strips all non digits, and removes leading 1 if us number
export function cleanPortalDid(did: string) {
  // if null or undefined, then return whatever it is
  if (!did) {
    return did;
  }

  return did.replace(/[^0-9]+/g, '')
            .replace(/^1(.{10})$/, '$1');
}

export function cleanDidToDigits(did: string | null | undefined) {
  if (!did) {
    return did;
  }

  return did.replace(/[^0-9]+/g, '');
}

export function cleanPortalInternationalAndUSDid(did: string) {
  if (!did) return did;
  return did.replace(/[^+0-9]+/g, '').replace(/^1(.{10})$/, '$1');
}

export function formatUSNumber(did: string) {
  if (!did) {
    return did;
  }

  const value = cleanPortalDid(did);
  if (value.length !== 10) {
    return value;
  }

  return `${value.substring(0, 3)}-${value.substring(3, 6)}-${value.substring(6, 10)}`;
}

export function formatTnE164(tnE164: string) {
  if (/[a-z]/i.test(tnE164)) {
    // This was used on e911 caller name, which can be a regular name, not just a numerical value.
    // Due to that it was hyphenating 10-character names, among other things.
    // I think it's safe that if there are any letters in the callerName, it's not something we should mess with.
    return tnE164;
  }

  if (tnE164 && tnE164.startsWith('+')) {
    const number = phoneUtil.parseAndKeepRawInput(tnE164);
    return phoneUtil.format(number, PhoneNumberFormat.INTERNATIONAL);
  }
  if (tnE164 && tnE164.length === 10) {
    return formatUSNumber(tnE164);
  }
  if (tnE164 && tnE164.length === 11 && tnE164.startsWith('1')) {
    return formatUSNumber(tnE164.substring(1))
  }
  if (tnE164 && tnE164.length === 6) {
    return `${tnE164.substring(0, 3)}-${tnE164.substring(3, 6)}`;
  }
  return tnE164;
}

export function formatTnE164ForRegion(tnE164: string, region: string = 'us') {
  const intlFormatedTn = formatTnE164(tnE164);

  if (!intlFormatedTn) {
    return intlFormatedTn;
  }

  // Both branches below slice a fixed number of raw characters off an
  // INTERNATIONAL-format string, which puts a space between the country code and the
  // national number ("+1 555-123-4567"). Slicing '+1' therefore left that separator
  // behind and returned " 555-123-4567"; trimStart drops it. The non-us branch slices
  // only '+', so today it lands on a digit and the trim is a no-op there — it is
  // applied anyway so the rule is "no leading separator", whatever a region's
  // international format looks like.
  if (region !== 'us') {
    // remove the +, for non-us regions
    if (intlFormatedTn.startsWith('+')) {
      return intlFormatedTn.substring(1).trimStart();
    }

    return intlFormatedTn;
  }

  // for us region, we need to remove the +1
  if (intlFormatedTn.startsWith('+1')) {
    return intlFormatedTn.substring(2).trimStart();
  }

  return intlFormatedTn;
}

export function getRegionCodeFromTnE164(tnE164: string): string | null {
  if (!tnE164) return null;

  return phoneUtil.getRegionCodeForNumber(phoneUtil.parse(tnE164)) ?? null;
}

// ***WARNING:  does NOT see if number is on net/mobile
function getDidTypeUS(tn: string): string {
  if (isTollFreeDid(tn)) {
    return DidTypes.US_TOLLFREE;
  }
  return DidTypes.US_LANDLINE;
}

// leaving as notes for future reference
// mapping what google-libphonenumber returns to our types instead of using prefixes
// function getDidTypeUK(tn: string): string {
//   if (tn.startsWith('01') || tn.startsWith('02')) {
//     return NumberTypes.GEOGRAPHIC;
//   }
//   if (tn.startsWith('03')) {
//     return NumberTypes.NONGEOGRAPHIC;
//   }
//   if (tn.startsWith('0800') || tn.startsWith('0808')) {
//     return NumberTypes.FREEPHONE;
//   }
//   if (tn.startsWith('07')) {
//     return NumberTypes.MOBILE;
//   }
//   if (tn.startsWith('09')) {
//     return NumberTypes.PREMIUM;
//   }
//   return NumberTypes.UNKNOWN;
// }

// leaving as notes for future reference
// mapping what google-libphonenumber returns to our types instead of using prefixes// function getDidTypeIT(tn: string): string {
//   if (tn.startsWith('0')) {
//     return NumberTypes.GEOGRAPHIC;
//   }
//   if (tn.startsWith('800')) {
//     return NumberTypes.FREEPHONE;
//   }
//   if (tn.startsWith('3')) {
//     return NumberTypes.MOBILE;
//   }
//   if (tn.startsWith('899')) {
//     return NumberTypes.PREMIUM;
//   }
//   return NumberTypes.UNKNOWN;
// }

export function getDidTypeFromTnE164(tnE164: string): string {
  if (!tnE164) return DidTypes.UNKNOWN;

  try {
    const regionCode = getRegionCodeFromTnE164(tnE164);
    if (!regionCode) return DidTypes.UNKNOWN;

    // US only uses 3 types right now and they are different from EMEA
    if (regionCode === 'US') {
      const number = phoneUtil.parseAndKeepRawInput(tnE164, regionCode);
      const nationalNumber = number.getNationalNumber();
      if (nationalNumber == null) return DidTypes.UNKNOWN;
      return getDidTypeUS(nationalNumber.toString());
    }

    const googleNumberType = phoneUtil.getNumberType(phoneUtil.parse(tnE164));
    switch (googleNumberType) {
      case PhoneNumberType.FIXED_LINE:
        return DidTypes.GEOGRAPHIC;
      case PhoneNumberType.MOBILE:
        return DidTypes.MOBILE;
      case PhoneNumberType.FIXED_LINE_OR_MOBILE:
        return DidTypes.GEOGRAPHIC;
      case PhoneNumberType.TOLL_FREE:
        return DidTypes.FREEPHONE;
      case PhoneNumberType.PREMIUM_RATE:
        return DidTypes.PREMIUM;
      case PhoneNumberType.UAN: // Universal Access Number
        return DidTypes.NONGEOGRAPHIC;
      case PhoneNumberType.SHARED_COST:
        return DidTypes.NONGEOGRAPHIC;
      case PhoneNumberType.VOIP:
        return DidTypes.NONGEOGRAPHIC;
      case PhoneNumberType.PERSONAL_NUMBER:
        return DidTypes.NONGEOGRAPHIC;
      case PhoneNumberType.VOICEMAIL:
        return DidTypes.NONGEOGRAPHIC;
      case PhoneNumberType.UNKNOWN:
        return DidTypes.UNKNOWN;
      default:
        return DidTypes.UNKNOWN;
    }
  } catch (e) {
    console.error('getDidTypeFromTnE164 error:', e);
    return DidTypes.UNKNOWN;
  }
}

// used for formatting the number to be copied to the clipboard
export function copyFormatTnE164ForRegion(tnE164: string, region: string = 'us') {
  // for us region, remove the +1 for us numbers
  if (region === 'us' && tnE164.startsWith('+1')) {
    return tnE164.substring(2);
  }

  // for non-us regions, remove the +
  if (region !== 'us' && tnE164.startsWith('+')) {
    return tnE164.substring(1);
  }

  // for all other cases, return the number as is
  return tnE164;
}

export function isValidUSNumber(did: string) {
  return /^1?[2-9][0-9]{2}[0-9]{7}$/.test(did);
}

export function isValidForwardNumber(did: string) {
  const cleanedNumber = did.replace(/[^0-9]+/g, '')
  return /^(([2-9][0-9]{7,})|(1[2-9][0-9]{9}))$/.test(cleanedNumber);
}

export function isValidInternationalAndUSNumber(did: string) {
  const cleanedNumber = cleanPortalInternationalAndUSDid(did);
  return /^(((\+|011)[0-9]{7,})|(1?[2-9][0-9]{9}))$/.test(cleanedNumber);
}

export function isValidInternationalOrLocalPhoneNumber(did: string, region: string) {
  try {
    if (isValidE164WithoutPlus(did)) {
      return true;
    }

    const phoneNumber = phoneUtil.parse(did, region);
    const tnE164 = phoneUtil.format(phoneNumber, PhoneNumberFormat.E164);
    return phoneUtil.isValidNumber(phoneUtil.parse(tnE164));
  } catch {
    return false;
  }
}

export function isValidE164WithoutPlus(did: string) {
  try {
    const phoneNumber = phoneUtil.parse(`+${did}`);
    const tnE164 = phoneUtil.format(phoneNumber, PhoneNumberFormat.E164);
    return phoneUtil.isValidNumber(phoneUtil.parse(tnE164));
  } catch {
    return false;
  }
}
export function isValidInternationalAndUSNumberLenient(did: string) {
  const cleanedNumber = cleanPortalInternationalAndUSDid(did);
  // Three valid patterns:
  // 1. (011|\+)[0-9]{7,} - International with explicit prefix (011 or +)
  // 2. [2-9][0-9]{6,} - Bare international (7+ digits starting with 2-9, like 44...)
  // 3. 1?[2-9][0-9]{9} - US numbers (10 digits, area code starts with 2-9, optional leading 1)
  return /^((011|\+)[0-9]{7,}|[2-9][0-9]{6,}|(1?[2-9][0-9]{9}))$/.test(cleanedNumber);
}

export function parseDids(input: string) {
  if ( !input ) {
    return [];
  }

  const dids = input.split(/[\s,]+/)
    .map(did => {
      return did.replace(/[^0-9]/g, '')
        // .replace(/^1/, ''); // strip 1 off of beginning if there
    });

  return dids.filter(did => did);
}

// If a ten digit number, return number with 1 in front of it
// otherwise, just return the number.
// This will help us in the future with international numbers
export function normalizePhoneNumberForDb(sipUser: string): string {
  if (isValidUSNumber(sipUser) && !sipUser.startsWith('1')) {
    return '1' + sipUser;
  }

  return sipUser;
}

export function normalizePhoneNumberForBlue(did: string | null | undefined) {
  if (!did) {
    return did;
  }

  // strip away the dashes but keep the + in front
  const cleanedDid = did.replace(/[^+0-9]+/g, '');
  if (cleanedDid.startsWith('+')) {
    return cleanedDid.substring(1);
  }
  if (cleanedDid.length === 10) {
    return '1' + cleanedDid;
  }
  return cleanedDid;
}

export function normalizePhoneNumberForDisplay(did: string) {
  if (!did) {
    return did;
  }

  if (did.length === 11) {
    return did.substring(1);
  }
  return `+${did}`;
}

export function validateDid(did: string) {
  if ( did.length !== 10 ) {
    return `All DIDs must be 10 digits.`;
  }

  return null;
}

export function validateDids(dids: string[]): { did: string, error: string | null}[] | null {
  if (dids.length < 1) {
    return null;
  }

  const didErrors = dids.map(did => {
    const didErrorMsg = validateDid(did);
    return didErrorMsg ? { did, error: didErrorMsg } : null;
  });
  return didErrors.filter((e): e is { did: string; error: string } => !!e);
}

export function splitTns(text: string): string[] {
  const ignoreDuplicates = (items: string[]): string[] => [...new Set<string>(items)];
  const ignoreInvalidNumbers = (numbers: string[]): string[] => numbers.filter(number => isValidUSNumber(number));

  let numbers = text.split(/[\n\r,;\t]/)
    .map(t => cleanPortalDid(t))
    .filter(t => !!t);

  numbers = ignoreDuplicates(numbers);
  numbers = ignoreInvalidNumbers(numbers);
  return numbers;
}

export function splitTnsNoUSCheck(text: string): string[] {
  const ignoreDuplicates = (items: string[]): string[] => [...new Set<string>(items)];

  let numbers = text.split(/[\n\r,;\t]/)
    .map(t => t.replace(/[^0-9]+/g, ''))
    .map(t => t.startsWith('1') ? t.substring(1) : t)
    .filter(t => !!t && t.length > 6);

  numbers = ignoreDuplicates(numbers);
  return numbers;
}

export function convertNumberToTenDigits(did: string): string {
  if (did.length === 10) return did;
  return did.replace(/^1/, '');
}

export function npaNxxFromDid(did: string) {
  // TODO: this is to replace the places we have been doing d.substring(0, 6) or d.slice(0, 6)
  // eventually we'll probably need to check for 1 in front or +countrycode
  return did.slice(0, 6);
}

export function portStatusToOrderStatus(portStatus: string) {
  if (!portStatus || ['PORTED', 'PENDING_ACTIVATION'].includes(portStatus)) {
    return 'IN_SERVICE';
  }
  return portStatus;
}

export function getCountryCode(country: string): string | null {
  const code = phoneUtil.getCountryCodeForRegion(country);
  return code ? '+' + code : null;
}

/**
 * Override map for non-standard country-name strings that don't match a real
 * ISO 3166-1 country display name (e.g. legacy combined values like
 * "US/Canada"). Add entries only when a real value seen in production can't
 * be resolved by the auto-built map below.
 */
const COUNTRY_NAME_OVERRIDES: Record<string, string> = {
  'US/Canada': 'US',
};

let countryNameToIsoCache: Record<string, string> | null = null;

function buildCountryNameToIsoMap(): Record<string, string> {
  if (countryNameToIsoCache) return countryNameToIsoCache;
  const map: Record<string, string> = {};
  const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
  for (const iso of phoneUtil.getSupportedRegions()) {
    const name = displayNames.of(iso);
    if (name && name !== iso) map[name] = iso;
  }
  countryNameToIsoCache = map;
  return map;
}

/**
 * Resolves a country display name (e.g. customer.service_country) to its
 * ISO 3166-1 alpha-2 code — "United Kingdom" → "GB". Combines an
 * Intl.DisplayNames-built reverse map (245 countries from
 * phoneUtil.getSupportedRegions()) with a small overrides map for
 * non-standard inputs like "US/Canada". Returns null when no match.
 */
export function getIsoCodeFromCountryName(countryName: string | null | undefined): string | null {
  if (!countryName) return null;
  return COUNTRY_NAME_OVERRIDES[countryName] ?? buildCountryNameToIsoMap()[countryName] ?? null;
}

/**
 * Resolves a country display name (e.g. customer.service_country) to its
 * digit calling code with no leading '+' — "United Kingdom" → "44". Returns
 * null when no match — callers should treat that as "no default available".
 */
export function getCallingCodeFromCountryName(countryName: string | null | undefined): string | null {
  const iso = getIsoCodeFromCountryName(countryName);
  if (!iso) return null;
  const code = phoneUtil.getCountryCodeForRegion(iso);
  return code ? String(code) : null;
}

export function getCountryFromFormattedDid(did: string | null | undefined) {
  if (!did) {
    return null;
  }

  if (did.indexOf(' ') === -1) {
    if (!did.startsWith('+')) {
      did = '+' + did;
    }

    return tnE164ToCountry(did);
  }

  const normalizedDid = did.startsWith('+') ? did.substring(1) : did;
  const countryCode = +normalizedDid.split(' ')[0];
  return phoneUtil.getRegionCodeForCountryCode(countryCode);
}

export function tnE164ToCountry(tnE164: string) {
  const parsedNumber = phoneUtil.parse(tnE164);
  // parse() rejects input without a +CC prefix, so the country code is always
  // present; 0 misses the region map and yields 'ZZ', same as undefined did.
  return phoneUtil.getRegionCodeForCountryCode(parsedNumber.getCountryCode() ?? 0);
}

export function tnE164ToCountryCode(tnE164: string): string | null {
  const parsedNumber = phoneUtil.parse(tnE164);
  const code = parsedNumber.getCountryCode();
  return code ? '+' + code : null;
}

export function extractCountryCodeAndNumberFromE164(tnE164: string) {
  const code = tnE164ToCountryCode(tnE164);
  if (code) {
    return {
      code,
      number: tnE164.slice(code.length),
    }
  }

  return null;
}

export function getTnByRegion(tnE164: string, region: string, addLeadingZero: boolean) {
  if (!tnE164 || !region) {
    return null;
  }

  const tnCountry = tnE164ToCountry(tnE164);
  if (region === tnCountry) {
    return translateInternationalDidToLocal({
      did: tnE164,
      region,
      addLeadingZero,
    })
  }
  return tnE164;
}

export function translateInternationalDidToLocal(input: { did: string, region: string, addLeadingZero: boolean }) {
  const { did, region, addLeadingZero } = input;
  const countryCode = getCountryCode(region);
  if (!countryCode) {
    // No calling code for the region means there is nothing to strip.
    return did;
  }
  return addLeadingZero ? '0' + did.slice(countryCode.length) : did.slice(countryCode.length);
}

// countries like Italy dont drop the leading 0. Also countries like spain that dont have a leading
// 0 but you still dont drop the any numbers. This is why we use the dropLeadingDigit boolean
export function translateLocalDidToInternational(input: { did: string, countryCode: string, dropLeadingDigit: boolean }) {
  const { did, countryCode, dropLeadingDigit } = input;
  if (dropLeadingDigit && did.startsWith('0')) {
    return countryCode + did.slice(1);
  }
  return countryCode + did;
}

export function cleanAndFormatInternationalDid(input: { country: string, did: string, isUsRegion: boolean }) {
  const { country, did, isUsRegion } = input
  const countryCode = getCountryCode(country)?.slice(1) || '1';
  let formattedNumber = cleanDidToDigits(did) as string;

  // check if number starts with country code with or without the +. If it doesn't we need to add it
  if (!formattedNumber.startsWith(countryCode)) {
    formattedNumber = translateLocalDidToInternational({
      did: formattedNumber,
      countryCode,
      dropLeadingDigit: true // TODO: will need to change if we add countries such as spain or italy where leading digit isnt dropped
    });
  }
  formattedNumber = '+' + formattedNumber;
  return formatTnE164ForRegion(formattedNumber, isUsRegion ? 'us' : 'eu');
}

export function addPlusToTnE164Number(tnE164: string) {
    const phoneNumber = phoneUtil.parse('+' + tnE164);
    if (!phoneUtil.isValidNumber(phoneNumber)) {
      return null;
    }
    return phoneUtil.format(phoneNumber, PhoneNumberFormat.E164);
}

export function getPhoneNumberE164Formatted(did: string, countryCodeOrRegion: string = 'US') {
  const normalized = countryCodeOrRegion.toLowerCase();
  if (normalized === 'eu') {
    const number = phoneUtil.parseAndKeepRawInput(`+${did}`);
    return phoneUtil.format(number, PhoneNumberFormat.E164);
  }
  const countryCode = normalized === 'us' ? 'US' : countryCodeOrRegion;
  const number = phoneUtil.parseAndKeepRawInput(did, countryCode);
  return phoneUtil.format(number, PhoneNumberFormat.E164);
}
